import React, { useState } from 'react';
import { useListContext, ReferenceArrayInput, AutocompleteArrayInput } from 'react-admin';
import { Box, Popper, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { colorChoices } from '@/constants/TagCategoryColorChoices';
import { Palette } from '@/themes/themeOverrides/Palette';
import { ReferenceArrayFilterSpinner } from '@/components/global/filters/ReferenceArrayFilterSpinner.jsx';
import { globalStyles } from '@/themes/commonStyles/global.js';
import isEqual from 'lodash/isEqual';
import TagPickerPopup from '@/components/global/tags/TagPickerPopup.jsx';
import { useTagPickerState } from '@/composables/useTagPickerState.js';
import ArrowUpCollapseIcon from '@/assets/icons/arrow-up-collapse-icon.svg?react';
import { tagPickerStyles } from '@/themes/commonStyles/tagPicker.js';
import { useTagPicker } from '@/composables/useTagPicker';
import { useSyncFilterFormValue } from '@/composables/useSyncFilterFormValue.js';
import { useTagNameSource } from '@/i18n/hooks.js';

const TagsFilterPopper = props => <Popper {...props} className='popper--tags-filter' />;

const StyledTagsFilter = styled('div')(({ theme, displayDeleteIcon }) => ({
    '& .tags-filter': {
        minWidth: '100px',
        position: 'relative',
        '&.open': {
            [theme.breakpoints.down('md')]: {
                maxHeight: 'max-content !important',
            },
            '& .MuiAutocomplete-root > .MuiFormControl-root.MuiFormControl-marginDense': {
                borderColor: Palette.black,
                boxShadow: `0 0 0 2px ${Palette.yellow.yellow200}`,
                '& .MuiFormLabel-root.Mui-focused': {
                    '& + .MuiInputBase-root,MuiOutlinedInput-root': {
                        outline: 'none !important',
                    },
                },
            },
        },
        '&.mobile': {
            boxShadow: 'none',
            '& .tags-filter__popup': {
                position: 'relative',
                maxHeight: 'fit-content',
                overflow: 'hidden',
                '& .actions': {
                    display: 'none',
                },
            },
        },
        '& .MuiAutocomplete-root': {
            ...globalStyles.arrayFilterAutocompleteInput,
            '& .MuiChip-root': {
                zIndex: '2',
                cursor: 'auto',
                padding: '4px 5px 4px 8px',
                '& .MuiChip-label': {
                    padding: '0 2px 0 0',
                },
                '& .MuiChip-deleteIcon': {
                    display: displayDeleteIcon ? 'block' : 'none',
                },
            },
        },
        '& .tags-filter__popup': {
            ...tagPickerStyles.popupContainer,
            minWidth: '520px',
            zIndex: 10,
            position: 'absolute',
            top: '100%',
            background: Palette.white,
            width: '100%',
            boxShadow: Palette.boxShadow.elevation2,
            borderRadius: '12px',
            display: 'none',
            padding: '0',
            [theme.breakpoints.down('md')]: {
                minWidth: 'unset',
            },
            '& .loading': {
                ...globalStyles.filterLoading,
            },
        },
        '& .tags-filter__categories': {
            ...tagPickerStyles.categoriesList,
            [theme.breakpoints.down('md')]: {
                padding: '24px',
            },
        },
        '& .arrow-up-collapse-icon': {
            position: 'absolute',
            right: '19px',
            top: '15px',
            zIndex: '2',
            height: '25px',
            borderRadius: '0',
            boxShadow: 'none',
            '& svg': {
                marginTop: '3px',
            },
        },
    },
}));

export const TagReferenceArrayFilter = ({
    source,
    label,
    optionText,
    inputText,
    optionValue,
    searchFields,
    filter = {},
    displayDeleteIcon = true,
    forceDesktopOnMobile = false,
}) => {
    const { setFilters, filterValues } = useListContext();
    useSyncFilterFormValue(source);
    const [inputFocused, setInputFocused] = useState(false);
    const onApply = ids => {
        setFilters({ ...filterValues, [source]: ids }, false);
    };
    const { open, setOpen, selectedIds, tempSelectedIds, setTempSelectedIds, handleTagToggle, handleApply, containerRef, popupRef } = useTagPickerState({
        initialSelectedIds: filterValues[source] || [],
        onApply,
        mobileMode: false,
    });

    const {
        tags,
        groupedTags,
        isLoading,
        mobileMode,
        toggleAllSelection,
        filterToQuery,
        setSearchText: setUnifiedSearchText,
    } = useTagPicker({
        searchFields,
        baseFilter: filter,
        forceDesktopOnMobile,
        selectedIds: tempSelectedIds,
    });
    React.useEffect(() => {
        if (!open) setUnifiedSearchText('');
    }, [open, setUnifiedSearchText]);
    const handleToggleAll = React.useCallback(() => {
        const newSelection = toggleAllSelection(tempSelectedIds);
        setTempSelectedIds(newSelection);
    }, [tempSelectedIds, toggleAllSelection, setTempSelectedIds]);
    const nameSource = useTagNameSource();

    if (isLoading && !inputFocused) {
        return <ReferenceArrayFilterSpinner />;
    }

    return (
        <StyledTagsFilter ref={containerRef} displayDeleteIcon={displayDeleteIcon}>
            <ReferenceArrayInput source={source} reference='tag' filter={filter}>
                <Box className={`tags-filter ${open ? 'open' : ''} ${mobileMode ? 'mobile' : ''}`}>
                    <Box
                        className='tags-filter__input'
                        onClick={() => {
                            setOpen(true);
                        }}
                    >
                        <AutocompleteArrayInput
                            fullWidth
                            label={label}
                            optionText={optionText}
                            inputText={inputText}
                            optionValue={optionValue}
                            filterToQuery={value => filterToQuery(value)}
                            onFocus={() => setInputFocused(true)}
                            onBlur={() => setInputFocused(false)}
                            PopperComponent={TagsFilterPopper}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const colorChoice = colorChoices.find(choice => choice.id === option.category?.color);
                                    const backgroundColor = colorChoice?.shades?.[200] || option.category?.color;
                                    return (
                                        <Chip
                                            key={option.id}
                                            label={option[nameSource] ?? option.name}
                                            size='small'
                                            {...getTagProps({ index })}
                                            style={{ backgroundColor }}
                                        />
                                    );
                                })
                            }
                        />
                        {!displayDeleteIcon && (
                            <Box className='arrow-up-collapse-icon'>
                                <ArrowUpCollapseIcon />
                            </Box>
                        )}
                    </Box>
                    <TagPickerPopup
                        open={open}
                        isLoading={isLoading}
                        tags={tags}
                        groupedTags={groupedTags}
                        tempSelectedIds={tempSelectedIds}
                        selectedIds={selectedIds}
                        setOpen={setOpen}
                        setTempSelectedIds={setTempSelectedIds}
                        handleTagToggle={handleTagToggle}
                        handleToggleAll={handleToggleAll}
                        handleApply={handleApply}
                        popupRef={popupRef}
                        categoriesClassName='tags-filter__categories'
                        containerClassName='tags-filter__popup'
                        actionsDisabled={isEqual(tempSelectedIds, selectedIds)}
                    />
                </Box>
            </ReferenceArrayInput>
        </StyledTagsFilter>
    );
};

TagReferenceArrayFilter.propTypes = {
    source: PropTypes.string,
    label: PropTypes.string,
    displayDeleteIcon: PropTypes.bool,
    forceDesktopOnMobile: PropTypes.bool,
    filter: PropTypes.object,
    searchFields: PropTypes.arrayOf(PropTypes.string),
};

export default TagReferenceArrayFilter;
